<template>
  <!-- Rendered only when the backend serves the per-gesture contract; an older
       backend (404 or the legacy { global, bySource } shape) hides the whole panel. -->
  <div v-if="available" class="gdm-section">
    <div class="gdm-section__title">
      <v-icon small class="mr-2">mdi-hand-back-right-outline</v-icon>
      Gesture detection
      <span class="caption grey--text ml-2">
        Analyst feedback per gesture: precision, recall and F1
      </span>
      <v-spacer />
      <v-select
        v-model="selectedSourceKey"
        :items="sourceOptions"
        dense
        outlined
        hide-details
        class="gdm-source-select"
        label="Source"
      />
    </div>

    <div v-if="loading" class="gdm-loading">
      <v-progress-circular indeterminate size="24" color="primary" />
    </div>

    <v-card v-else outlined class="gdm-card">
      <div v-if="!rows.length && !retiredRow" class="gdm-empty">
        <v-icon large color="grey lighten-1">mdi-gesture-tap</v-icon>
        <div class="subtitle-2 grey--text mt-2">No gesture feedback yet</div>
        <div class="caption grey--text">
          Mark detected gestures as correct or incorrect, or report a missing one, to start
          measuring detection quality.
        </div>
      </div>

      <template v-else>
        <v-data-table
          :headers="headers"
          :items="rows"
          :items-per-page="-1"
          sort-by="total"
          sort-desc
          dense
          hide-default-footer
          class="gdm-table"
        >
          <template #[`item.label`]="{ item }">
            <span :title="item.gesture_name">{{ item.label }}</span>
          </template>
          <template #[`item.precision`]="{ item }">
            <span class="gdm-mono">{{ formatRatio(item.precision) }}</span>
          </template>
          <template #[`item.recall`]="{ item }">
            <span class="gdm-mono" :title="recallTitle(item)">{{ formatRatio(item.recall) }}</span>
          </template>
          <template #[`item.f1`]="{ item }">
            <span class="gdm-mono" :title="recallTitle(item)">{{ formatRatio(item.f1) }}</span>
          </template>

          <template #[`body.append`]>
            <tr v-if="retiredRow" class="gdm-row--retired">
              <td>
                <span :title="retiredRow.title">Retired gesture ids</span>
                <v-chip x-small outlined class="ml-1">retired</v-chip>
              </td>
              <td class="text-end">{{ retiredRow.tp }}</td>
              <td class="text-end">{{ retiredRow.fp }}</td>
              <td class="text-end">{{ retiredRow.fn }}</td>
              <td class="text-end gdm-mono">{{ formatRatio(retiredRow.precision) }}</td>
              <td class="text-end gdm-mono" :title="recallTitle(retiredRow)">
                {{ formatRatio(retiredRow.recall) }}
              </td>
              <td class="text-end gdm-mono" :title="recallTitle(retiredRow)">
                {{ formatRatio(retiredRow.f1) }}
              </td>
              <td class="text-end">{{ retiredRow.total }}</td>
            </tr>
            <tr v-if="totalsRow" class="gdm-row--totals">
              <td>All gestures</td>
              <td class="text-end">{{ totalsRow.tp }}</td>
              <td class="text-end">{{ totalsRow.fp }}</td>
              <td class="text-end">{{ totalsRow.fn }}</td>
              <td class="text-end gdm-mono">{{ formatRatio(totalsRow.precision) }}</td>
              <td class="text-end gdm-mono" :title="recallTitle(totalsRow)">
                {{ formatRatio(totalsRow.recall) }}
              </td>
              <td class="text-end gdm-mono" :title="recallTitle(totalsRow)">
                {{ formatRatio(totalsRow.f1) }}
              </td>
              <td class="text-end">{{ totalsRow.total }}</td>
            </tr>
          </template>
        </v-data-table>
      </template>

      <!-- Outside the empty-state branch: legacy rows without gesture_name are excluded
           from every count, so they must stay visible even when they are the only rows. -->
      <div v-if="unattributed > 0" class="gdm-unattributed">
        Unattributed legacy rows: {{ unattributed }} (no gesture name; excluded from every count)
      </div>

      <div v-if="caveats.length" class="gdm-caveats">
        <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
        <span>
          <span v-for="(c, i) in caveats" :key="i" class="gdm-caveat">{{ c }}</span>
        </span>
      </div>
    </v-card>

    <!-- Labels vs current run: only meaningful for one source -->
    <v-card v-if="selectedSource && rematchAvailable" outlined class="gdm-card gdm-rematch">
      <div class="gdm-rematch__title">
        <v-icon small class="mr-1">mdi-swap-horizontal</v-icon>
        Labels vs current run
        <span class="caption grey--text ml-2">
          Do the stored labels still land on a detection of the latest analysis?
        </span>
      </div>

      <div v-if="rematchLoading" class="gdm-loading gdm-loading--inline">
        <v-progress-circular indeterminate size="20" color="primary" />
      </div>

      <template v-else-if="rematch">
        <v-alert v-if="runChanged" type="warning" dense text class="gdm-rematch__alert">
          The analysis has been re-run since these labels were written (labels run
          <span class="gdm-mono">{{ shortHash(labelsRunHash) }}</span
          >, current run <span class="gdm-mono">{{ shortHash(currentRunHash) }}</span
          >). Lost labels are episodes the current run no longer produces, not analyst errors.
        </v-alert>
        <div v-else-if="sameConfigHash === true" class="gdm-rematch__hint">
          Labels and current run share the same configuration (<span class="gdm-mono">{{
            shortHash(currentRunHash)
          }}</span
          >).
        </div>
        <div v-else class="gdm-rematch__hint">
          Run identity undecidable: some labels or the current run carry no configuration
          hash (current run <span class="gdm-mono">{{ shortHash(currentRunHash) }}</span
          >).
        </div>

        <div v-if="!rematchRows.length" class="gdm-rematch__hint">
          No gesture labels stored for this source.
        </div>
        <v-simple-table v-else dense class="gdm-rematch__table">
          <thead>
            <tr>
              <th>Gesture</th>
              <th class="text-end">Labels</th>
              <th class="text-end">Matched</th>
              <th class="text-end">Lost</th>
              <th class="text-end">Missing, now detected</th>
              <th class="text-end">Still missing</th>
              <th class="text-end">No time coordinate</th>
              <th class="text-end">Current episodes</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in rematchRows" :key="r.gesture_name">
              <td :title="r.gesture_name">{{ r.label }}</td>
              <td class="text-end">{{ r.labels }}</td>
              <td class="text-end">{{ r.matched }}</td>
              <td class="text-end" :class="{ 'gdm-lost': r.lost > 0 }">{{ r.lost }}</td>
              <td class="text-end">{{ r.missingNowDetected }}</td>
              <td class="text-end">{{ r.missingStillMissing }}</td>
              <td class="text-end">{{ r.noTimeCoordinate }}</td>
              <td class="text-end">{{ r.currentEpisodes }}</td>
            </tr>
          </tbody>
        </v-simple-table>

        <div v-if="rematchCaveats.length" class="gdm-caveats">
          <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
          <span>
            <span v-for="(c, i) in rematchCaveats" :key="i" class="gdm-caveat">{{ c }}</span>
          </span>
        </div>
      </template>
    </v-card>
  </div>
</template>

<script>
import DetectionFeedbackService from '@/services/DetectionFeedbackService';
import { gestureNameToLabel } from '@/utils/gestureLabels';

const ALL_SOURCES = '__all__';

/**
 * Locate the per-gesture contract: at the top level, under { data }, or inside the
 * existing { data: { global, bySource } } envelope. Null means "not served".
 */
function unwrap(response) {
  const body = response && response.data;
  if (!body || typeof body !== 'object') return null;
  if (Array.isArray(body.byGesture)) return body;
  const inner = body.data && typeof body.data === 'object' ? body.data : null;
  if (!inner) return null;
  if (Array.isArray(inner.byGesture)) return inner;
  if (inner.global && Array.isArray(inner.global.byGesture)) {
    return { ...inner.global, bySource: inner.bySource };
  }
  return null;
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function ratio(v) {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

const PRECISION_ONLY_TITLE =
  'Precision only: no missing-gesture labels yet, so recall and F1 cannot be computed';

/**
 * Recall needs negative evidence ('missing' labels). With fn == 0 the ratio tp / (tp + 0)
 * is trivially 1 and says nothing, so it is masked here even if an older backend still
 * serves it; the reviewed scope is not stored, so fn == 0 means unknown, not perfect.
 */
function countsFrom(obj) {
  const tp = num(obj.tp);
  const fp = num(obj.fp);
  const fn = num(obj.fn);
  const total = obj.total === undefined ? tp + fp + fn : num(obj.total);
  const precisionOnly = fn === 0 && tp + fp > 0;
  const caveats = Array.isArray(obj.caveats)
    ? obj.caveats.filter((c) => typeof c === 'string' && c)
    : [];
  return {
    tp,
    fp,
    fn,
    total,
    precision: ratio(obj.precision),
    recall: precisionOnly ? null : ratio(obj.recall),
    f1: precisionOnly ? null : ratio(obj.f1),
    precisionOnly,
    caveats,
  };
}

export default {
  name: 'GestureDetectionMetricsPanel',

  props: {
    /**
     * Extra sources for the selector, e.g. the labeled videos of the parent view:
     * [{ source_type, source_id, video_name? }]. Merged with the sources the
     * endpoint itself reports (if any).
     */
    sources: { type: Array, default: () => [] },
  },

  data() {
    return {
      // null = not known yet; false = older backend (hide); true = contract served.
      available: null,
      loading: false,
      payload: null,
      selectedSourceKey: ALL_SOURCES,
      rematch: null,
      rematchLoading: false,
      rematchAvailable: true,
    };
  },

  computed: {
    headers() {
      return [
        { text: 'Gesture', value: 'label', sortable: true },
        { text: 'TP', value: 'tp', align: 'end', width: 70 },
        { text: 'FP', value: 'fp', align: 'end', width: 70 },
        { text: 'FN', value: 'fn', align: 'end', width: 70 },
        { text: 'Precision', value: 'precision', align: 'end', width: 100 },
        { text: 'Recall', value: 'recall', align: 'end', width: 100 },
        { text: 'F1', value: 'f1', align: 'end', width: 90 },
        { text: 'Total', value: 'total', align: 'end', width: 80 },
      ];
    },

    rows() {
      const list = (this.payload && this.payload.byGesture) || [];
      return list
        .filter((g) => g && typeof g === 'object' && g.gesture_name)
        .map((g) => ({
          gesture_name: g.gesture_name,
          label: g.label || gestureNameToLabel(g.gesture_name),
          ...countsFrom(g),
        }));
    },

    /**
     * `retired` may come as one aggregate bucket ({ tp, fp, fn, ... , gesture_names? })
     * or as a map keyed by retired id; both collapse into one muted row.
     */
    retiredRow() {
      const retired = this.payload && this.payload.retired;
      if (!retired || typeof retired !== 'object') return null;
      const hasCounts = ['tp', 'fp', 'fn', 'total'].some((k) => retired[k] !== undefined);
      let counts;
      let names = [];
      if (hasCounts) {
        counts = countsFrom(retired);
        names = Array.isArray(retired.gesture_names) ? retired.gesture_names : [];
      } else {
        const buckets = Object.entries(retired).filter(
          ([, v]) => v && typeof v === 'object' && !Array.isArray(v)
        );
        if (!buckets.length) return null;
        names = buckets.map(([k]) => k);
        const sum = buckets.reduce(
          (acc, [, v]) => {
            const c = countsFrom(v);
            return {
              tp: acc.tp + c.tp,
              fp: acc.fp + c.fp,
              fn: acc.fn + c.fn,
              total: acc.total + c.total,
            };
          },
          { tp: 0, fp: 0, fn: 0, total: 0 }
        );
        counts = {
          ...sum,
          ...this.derivedRatios(sum),
          precisionOnly: sum.fn === 0 && sum.tp + sum.fp > 0,
          caveats: [],
        };
      }
      if (!counts.total) return null;
      return {
        ...counts,
        title: names.length ? names.join(', ') : 'ids no longer in the catalogue',
      };
    },

    /**
     * The backend puts the aggregate counts at the top level of `global` (same shape
     * as CoherenceLabelService.getMetrics); a nested `totals` is accepted too.
     */
    totalsRow() {
      const p = this.payload;
      if (!p || typeof p !== 'object') return null;
      const totals = p.totals && typeof p.totals === 'object' ? p.totals : p;
      const counts = countsFrom(totals);
      return counts.total ? counts : null;
    },

    caveats() {
      const list = this.payload && this.payload.caveats;
      return Array.isArray(list) ? list.filter((c) => typeof c === 'string' && c) : [];
    },

    /** Legacy rows with no gesture_name; the backend keeps them out of every count. */
    unattributed() {
      return num(this.payload && this.payload.unattributed);
    },

    sourceOptions() {
      const seen = new Map();
      const add = (s) => {
        if (!s || !s.source_type || s.source_id === undefined || s.source_id === null) return;
        const key = `${s.source_type}:${s.source_id}`;
        if (seen.has(key)) return;
        const kind = s.source_type === 'manual_analysis' ? 'Manual' : 'Interview';
        const name = s.video_name ? ` · ${s.video_name}` : '';
        seen.set(key, { value: key, text: `${kind} #${s.source_id}${name}` });
      };
      ((this.payload && this.payload.bySource) || []).forEach(add);
      (this.sources || []).forEach(add);
      return [{ value: ALL_SOURCES, text: 'All sources' }, ...seen.values()];
    },

    selectedSource() {
      if (!this.selectedSourceKey || this.selectedSourceKey === ALL_SOURCES) return null;
      const idx = this.selectedSourceKey.indexOf(':');
      if (idx < 0) return null;
      return {
        sourceType: this.selectedSourceKey.slice(0, idx),
        sourceId: Number(this.selectedSourceKey.slice(idx + 1)),
      };
    },

    /** GestureRematchReport.current_run (null when the source has no gesture_results doc). */
    currentRunHash() {
      const run = this.rematch && this.rematch.current_run;
      return (run && run.analysis_config_hash) || null;
    },

    /** Distinct hashes the labels were stamped with; null entries are pre-Block-A rows. */
    stampedHashes() {
      const runs = (this.rematch && this.rematch.stamped_runs) || [];
      const set = new Set();
      runs.forEach((r) => {
        if (r && typeof r === 'object') set.add(r.analysis_config_hash || null);
      });
      return [...set];
    },

    labelsRunHash() {
      const known = this.stampedHashes.filter(Boolean);
      if (known.length === 1 && this.stampedHashes.length === 1) return known[0];
      return null;
    },

    /** true / false from the backend; null when it cannot be decided (missing hashes). */
    sameConfigHash() {
      if (!this.rematch) return null;
      const v = this.rematch.same_config_hash;
      return typeof v === 'boolean' ? v : null;
    },

    runChanged() {
      return this.sameConfigHash === false;
    },

    rematchCaveats() {
      const list = this.rematch && this.rematch.caveats;
      return Array.isArray(list) ? list.filter((c) => typeof c === 'string' && c) : [];
    },

    rematchRows() {
      const summary = this.rematch && this.rematch.rematch;
      const list = (summary && summary.byGesture) || [];
      return list
        .filter((g) => g && typeof g === 'object' && g.gesture_name)
        .map((g) => ({
          gesture_name: g.gesture_name,
          label: g.label || gestureNameToLabel(g.gesture_name),
          labels: num(g.labels),
          matched: num(g.matched),
          lost: num(g.lost),
          missingNowDetected: num(g.missing_now_detected),
          missingStillMissing: num(g.missing_still_missing),
          noTimeCoordinate: num(g.no_time_coordinate),
          currentEpisodes: num(g.current_episodes),
        }))
        .sort((a, b) => b.matched + b.lost - (a.matched + a.lost));
    },
  },

  watch: {
    selectedSourceKey() {
      this.load();
    },
  },

  created() {
    this.load();
  },

  methods: {
    async load() {
      this.loading = true;
      try {
        const filters = this.selectedSource || {};
        const response = await DetectionFeedbackService.getGestureMetrics(filters);
        const payload = unwrap(response);
        if (!payload) {
          // Endpoint exists but serves the legacy shape: nothing per gesture to show.
          this.available = false;
          return;
        }
        this.payload = payload;
        this.available = true;
        await this.loadRematch();
      } catch (e) {
        // 404 (older backend) or any other failure: hide rather than error the page.
        this.available = false;
      } finally {
        this.loading = false;
      }
    },

    async loadRematch() {
      this.rematch = null;
      if (!this.selectedSource || !this.rematchAvailable) return;
      this.rematchLoading = true;
      try {
        const response = await DetectionFeedbackService.getGestureRematch(this.selectedSource);
        const body = response && response.data;
        // GestureRematchReport comes under { data }; tolerate it at the top level too.
        const data = body && body.rematch ? body : body && body.data;
        this.rematch = data && typeof data === 'object' && data.rematch ? data : null;
      } catch (e) {
        const status = e && e.response && e.response.status;
        // Only a missing route means an older backend; other errors just leave the block empty.
        if (status === 404) this.rematchAvailable = false;
        this.rematch = null;
      } finally {
        this.rematchLoading = false;
      }
    },

    derivedRatios({ tp, fp, fn }) {
      const precision = tp + fp > 0 ? tp / (tp + fp) : null;
      const recall = fn > 0 ? tp / (tp + fn) : null;
      const f1 =
        precision !== null && recall !== null && precision + recall > 0
          ? (2 * precision * recall) / (precision + recall)
          : null;
      return { precision, recall, f1 };
    },

    /** Tooltip for the recall / F1 cells of a row that has no 'missing' labels. */
    recallTitle(row) {
      return row && row.precisionOnly ? PRECISION_ONLY_TITLE : undefined;
    },

    /** Ratios come as fractions (0..1) like the existing metrics service. */
    formatRatio(v) {
      if (v === null || v === undefined) return '—';
      const n = Number(v);
      if (!Number.isFinite(n)) return '—';
      return `${(n * 100).toFixed(1)}%`;
    },

    shortHash(hash) {
      if (!hash) return 'unknown';
      return String(hash).slice(0, 10);
    },
  },
};
</script>

<style scoped>
.gdm-section {
  margin-bottom: 32px;
}

.gdm-section__title {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #555;
  padding: 0 4px;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;
}

.gdm-source-select {
  max-width: 320px;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 400;
}

.gdm-card {
  border-radius: 8px;
}

.gdm-table {
  background: transparent !important;
}

.gdm-loading {
  display: flex;
  justify-content: center;
  padding: 32px 0;
}

.gdm-loading--inline {
  padding: 16px 0;
}

.gdm-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
  text-align: center;
}

.gdm-row--retired td {
  color: #9e9e9e;
  font-style: italic;
}

.gdm-row--totals td {
  font-weight: 700;
  border-top: 2px solid rgba(0, 0, 0, 0.12);
}

.gdm-mono {
  font-family: 'Roboto Mono', monospace;
  font-size: 12px;
}

.gdm-caveats {
  display: flex;
  align-items: flex-start;
  padding: 8px 16px 12px;
  font-size: 12px;
  color: #757575;
}

.gdm-caveat {
  display: block;
}

.gdm-unattributed {
  padding: 8px 16px 0;
  font-size: 12px;
  color: #9e9e9e;
  font-style: italic;
}

.gdm-rematch {
  margin-top: 16px;
  padding: 16px 20px;
}

.gdm-rematch__title {
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #555;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.gdm-rematch__alert {
  font-size: 12px;
  margin-bottom: 8px;
}

.gdm-rematch__hint {
  font-size: 12px;
  color: #757575;
  margin-bottom: 8px;
}

.gdm-rematch__table {
  background: transparent !important;
}

.gdm-lost {
  color: #f44336;
  font-weight: 700;
}
</style>
